/** Eksportene under «concerts» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/concerts/`. */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const col = (
  group: string,
  key: string,
  type: ExportColumnMeta['type'],
  extra: Omit<ExportColumnMeta, 'key' | 'labelKey' | 'type'> = {},
): ExportColumnMeta => ({
  key,
  labelKey: `export.concerts.${group}.columns.${key}`,
  type,
  ...extra,
});

const MONEY: ExportColumnMeta['requires'] = ['concert_budget:read'];

/**
 * KONSERTER: lista på «Konserter» (`/shows`) med samme filter (når, søk, merkelapp, status,
 * ansvarlig, måneden i kalendervisningen, og i et byrå egne/solgte) og de kolonnene brukeren har
 * valgt der — nøklene er tabellens (`tableKey: 'org-concerts'`). Avtale og break-even bare med
 * `concert_budget:read`, som på skjermen. `organiser` finnes bare i eksporten (byråets solgte).
 */
const LIST: ExportMeta = {
  id: 'concerts.list',
  domain: 'concerts',
  kind: 'table',
  titleKey: 'export.concerts.list.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['concert:list'],
  guest: false,
  tableKey: 'org-concerts',
  columns: [
    col('list', 'date', 'date'),
    col('list', 'artist', 'text'),
    col('list', 'venue', 'text'),
    col('list', 'city', 'text'),
    col('list', 'status', 'status'),
    col('list', 'deal', 'text', { requires: MONEY }),
    col('list', 'capacity', 'integer'),
    col('list', 'breakEven', 'integer', { requires: MONEY }),
    col('list', 'advance', 'status'),
    col('list', 'responsible', 'text'),
    col('list', 'tag', 'text', { defaultHidden: true }),
    col('list', 'title', 'text', { defaultHidden: true }),
    col('list', 'format', 'text', { defaultHidden: true }),
    col('list', 'support', 'text', { defaultHidden: true }),
    col('list', 'address', 'text', { defaultHidden: true }),
    col('list', 'layout', 'text', { defaultHidden: true }),
    col('list', 'doors', 'time', { defaultHidden: true }),
    col('list', 'curfew', 'time', { defaultHidden: true }),
    col('list', 'getIn', 'time', { defaultHidden: true }),
    col('list', 'expectedAudience', 'integer', { defaultHidden: true }),
    col('list', 'announcedOn', 'date', { defaultHidden: true }),
    col('list', 'onSaleOn', 'date', { defaultHidden: true }),
    col('list', 'holdUntil', 'date', { defaultHidden: true }),
    col('list', 'notes', 'multiline', { defaultHidden: true }),
    col('list', 'createdAt', 'datetime', { defaultHidden: true }),
    col('list', 'updatedAt', 'datetime', { defaultHidden: true }),
    col('list', 'organiser', 'text', { defaultHidden: true }),
  ],
  landscape: true,
};

/**
 * TURNÉOVERSIKT: konsertene i lista samlet per turné (merkelappen «Ny konsert» setter på alle
 * datoene), én del per turné med datoene i rekkefølge, og økonomien per dato for den som ser den.
 */
const TOUR: ExportMeta = {
  id: 'concerts.tour',
  domain: 'concerts',
  kind: 'document',
  titleKey: 'export.concerts.tour.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['concert:list'],
  guest: false,
  sections: [
    { key: 'dates', labelKey: 'export.concerts.tour.sections.dates' },
    { key: 'economy', labelKey: 'export.concerts.tour.sections.economy', requires: MONEY },
  ],
  landscape: true,
};

/**
 * DAGSARK FOR EN KONSERT: konsertens fakta, kjøreplanen, artistene, kontaktene med kosthold og
 * allergier, reisen, hotellet og husets poster. Ett ark per konsert; fra konserten, og for alle i
 * lista eller de merkede.
 */
const DAY_SHEET: ExportMeta = {
  id: 'concerts.day-sheet',
  domain: 'concerts',
  kind: 'document',
  titleKey: 'export.concerts.daySheet.title',
  formats: ['pdf', 'docx'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['concert:read'],
  guest: false,
  sections: [
    { key: 'schedule', labelKey: 'export.concerts.daySheet.sections.schedule' },
    { key: 'artists', labelKey: 'export.concerts.daySheet.sections.artists' },
    { key: 'contacts', labelKey: 'export.concerts.daySheet.sections.contacts' },
    { key: 'travel', labelKey: 'export.concerts.daySheet.sections.travel' },
    { key: 'stay', labelKey: 'export.concerts.daySheet.sections.stay' },
    { key: 'crew', labelKey: 'export.concerts.daySheet.sections.crew' },
  ],
};

/**
 * BUDSJETT MED SCENARIER: Budsjett-fanen på papir — inntektene, kostnadslinjene med sum, én tabell
 * per scenario (lavt, estimat, utsolgt) med resultatet som sum, break-even og avtalene. Erstatter
 * den gamle CSV-knappen. Bare arrangørens (`concert_budget:read`).
 */
const BUDGET: ExportMeta = {
  id: 'concerts.budget',
  domain: 'concerts',
  kind: 'document',
  titleKey: 'export.concerts.budget.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['concert:read', 'concert_budget:read'],
  guest: false,
  sections: [
    { key: 'inputs', labelKey: 'export.concerts.budget.sections.inputs' },
    { key: 'costs', labelKey: 'export.concerts.budget.sections.costs' },
    { key: 'scenarios', labelKey: 'export.concerts.budget.sections.scenarios' },
    { key: 'deals', labelKey: 'export.concerts.budget.sections.deals' },
  ],
};

/**
 * OPPGJØR SOM DOKUMENT: inntektene, kostnadene (budsjett mot faktisk), delingen med artisten og
 * resultatet, fra det lagrede oppgjøret. Bare arrangørens; konserter uten oppgjør er ikke med.
 */
const SETTLEMENT: ExportMeta = {
  id: 'concerts.settlement',
  domain: 'concerts',
  kind: 'document',
  titleKey: 'export.concerts.settlement.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['concert:read', 'concert_budget:read'],
  guest: false,
  sections: [
    { key: 'revenue', labelKey: 'export.concerts.settlement.sections.revenue' },
    { key: 'costs', labelKey: 'export.concerts.settlement.sections.costs' },
    { key: 'split', labelKey: 'export.concerts.settlement.sections.split' },
    { key: 'result', labelKey: 'export.concerts.settlement.sections.result' },
  ],
};

/**
 * OPPGJØRET TIL BYRÅET: bare artistens del (billetter, avgift, netto, avtalte kostnader når avtalen
 * regnes etter kostnader, avtalen og det artisten får). Leien, linjene og arrangørens resultat står
 * aldri her. Erstatter «Skriv ut» i oppgjøret; det er arket som sendes byrået.
 */
const STATEMENT: ExportMeta = {
  id: 'concerts.settlement-statement',
  domain: 'concerts',
  kind: 'document',
  titleKey: 'export.concerts.statement.title',
  formats: ['pdf', 'docx'],
  scopes: ['object'],
  requires: ['concert:read', 'concert_budget:read'],
  guest: false,
};

/**
 * BUD OG STATUS: én rad per artist på konsertene — konsertens status og budet til byrået (status,
 * beløp og avtale der leseren får se beløp, sendt, frist, svar). Fra lista og fra konserten.
 */
const BIDS: ExportMeta = {
  id: 'concerts.bids',
  domain: 'concerts',
  kind: 'table',
  titleKey: 'export.concerts.bids.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'filter', 'selection'],
  requires: ['concert:list', 'performance:list'],
  guest: false,
  columns: [
    col('bids', 'date', 'date'),
    col('bids', 'venue', 'text'),
    col('bids', 'city', 'text'),
    col('bids', 'concertStatus', 'status'),
    col('bids', 'artist', 'text'),
    col('bids', 'billing', 'status'),
    col('bids', 'agency', 'text'),
    col('bids', 'bidStatus', 'status'),
    /* 250.19: beløpene følger beløpsregelen, i dialogen og i fila. */
    col('bids', 'amount', 'money', { rule: 'booking_amounts' }),
    col('bids', 'deal', 'text'),
    col('bids', 'sentAt', 'date'),
    col('bids', 'respondsBy', 'date'),
    col('bids', 'answeredAt', 'date', { defaultHidden: true }),
    col('bids', 'availability', 'status', { defaultHidden: true }),
    col('bids', 'askingFee', 'money', { defaultHidden: true, rule: 'booking_amounts' }),
    col('bids', 'sentBy', 'text', { defaultHidden: true }),
    col('bids', 'responseNote', 'multiline', { defaultHidden: true }),
  ],
  landscape: true,
};

export const CONCERTS_EXPORTS: readonly ExportMeta[] = [
  LIST,
  TOUR,
  DAY_SHEET,
  BUDGET,
  SETTLEMENT,
  STATEMENT,
  BIDS,
];
